import { getTxnInfo } from '@api/form-verification';
import { StartingProcessDialogType } from '@containers/form-verification/form/types';
import { TxnState } from '@global/constants/verification';
import usePreStartQuestionList from '@global/hooks/usePreStartQuestionList';
import { Box, Button, CircularProgress, Dialog, Grid, Typography } from '@mui/material';
import { updateMiscStore } from '@redux/slices/misc';
import { store } from '@redux/store';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { basicDetailsReqd, handleProcessTransaction } from '../helpers';
import styles from './styles';

export default function StartProcess({
  openStartingProcessDialog,
  setOpenStartingProcessDialog
}: {
  openStartingProcessDialog: StartingProcessDialogType,
  setOpenStartingProcessDialog: (value: StartingProcessDialogType) => void
}) {
  const {
    preStartQuestionComponent,
    onPreStartAnswersSubmission,
    preStartQuestionIsDisabled,
    preStartQuestionListLength,
    loadingPreQuestionList
  } = usePreStartQuestionList();

  const profile = useSelector((s: any) => s.profile);
  const formData = useSelector((s: any) => s.formData);
  const { orgInfo = {}, txnId, txnInfo } = formData ?? {};
  const [loading, setLoading] = useState(false);

  const isDisabled = () => {
    if (loading) {
      return true;
    }
    return Number(preStartQuestionListLength) > 0 ? preStartQuestionIsDisabled : null;
  };

  const onStartClick = async () => {
    setLoading(true);
    const res = Number(preStartQuestionListLength) > 0 ? await onPreStartAnswersSubmission() : true;
    if (res) {
      // await refreshTransactionInfo();
      const txnRes = await getTxnInfo(txnId, orgInfo?.orgId);

      // process transaction if profile exists
      const profileVerified = profile?.profileDetails?.name ? true : false;

      if (
        txnRes?.data?.state === TxnState.NOT_INITIALIZED &&
        profileVerified &&
        !basicDetailsReqd(txnInfo)
      ) {
        await handleProcessTransaction(orgInfo?.orgId, txnId);
      } else {
        store.dispatch(updateMiscStore({ startButtonClicked: txnId }));
      }

      if (openStartingProcessDialog?.callback) {
        await openStartingProcessDialog.callback();
        setLoading(false);
      } else {
        setLoading(false);
      }
      setOpenStartingProcessDialog(null);
    } else {
      setLoading(false);
    }
  };

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={Boolean(openStartingProcessDialog)}
      onClose={() => {
        setOpenStartingProcessDialog(null);
      }}
      sx={{ padding: '1em', margin: 'auto' }}>
      <Grid container direction="column">
        {loadingPreQuestionList ? (
          <Box sx={{ minHeight: '30vh', display: 'grid', placeItems: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={styles.pending.topRow}>
              <Typography variant="body1" sx={{ fontWeight: '600' }}>
                {Number(preStartQuestionListLength) <= 0
                  ? 'Proceed to form'
                  : 'Form Purpose and Details'}
              </Typography>
            </Box>
            {Number(preStartQuestionListLength) <= 0 ? (
              <Box>
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'left',
                    m: '2rem 1.15rem'
                  }}>
                  Click <b>Start</b> to start filling the form
                </Typography>
              </Box>
            ) : (
              preStartQuestionComponent
            )}
          </>
        )}
      </Grid>
      {!loadingPreQuestionList && (
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={styles.pending.bottomRow}>
          <Button
            sx={{
              typography: 'body1',
              borderRadius: '4px',
              p: '0.5rem 1rem',
              color: 'primary.main',
              width: 'fit-content'
            }}
            disabled={loading}
            onClick={async () => {
              setOpenStartingProcessDialog(null);
            }}>
            Cancel
          </Button>
          <Button
            sx={{
              typography: 'body1',
              borderRadius: '4px',
              p: '0.5rem 1rem',
              color: 'primary.main',
              width: 'fit-content'
            }}
            id="start"
            startIcon={
              loading && (
                <CircularProgress style={{ height: '1.5rem', width: '1.5rem', color: 'grey' }} />
              )
            }
            disabled={isDisabled()}
            onClick={onStartClick}>
            Start
          </Button>
        </Grid>
      )}
    </Dialog>
  );
}
